/**
 * syncApi — web-session, CSRF-protected sync endpoints for reading state that
 * is NOT part of TASK-009's server-authoritative surface (highlights,
 * progress, reading position). Quiz sessions/attempts are NOT here anymore:
 * TASK-009 Phase 3/5 made quiz sessions server-authoritative end to end
 * (Reader::QuizSessionsController creates sessions, grades answers, and
 * writes quiz_attempts itself — see ./quizApi.ts) — the web client calling
 * `/web/sync/quiz_sessions` or `/web/sync/quiz_attempts` would just write a
 * second, redundant (and for attempts, un-graded/client-trusted) copy of data
 * the server already owns. The mobile client is unaffected: it still uses
 * those endpoints (see TASK-009 scope — mobile stays offline/fat-client).
 */

import type {
  ReaderRenderHighlight,
  ReaderRenderHighlightColor,
  ReaderRenderHighlightStyle,
} from '@arianna/reader-render'

export function getCsrfToken(): string {
  return (
    (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ''
  )
}

function jsonHeaders(): HeadersInit {
  return {
    'Content-Type': 'application/json',
    'X-CSRF-Token': getCsrfToken(),
  }
}

/**
 * Best-effort fire-and-forget POST — a transient network failure (offline
 * blip, request aborted by navigation) must not surface as an unhandled
 * promise rejection or block the reading UI. Callers that need the response
 * body (syncHighlights) handle their own try/catch instead of using this.
 */
async function postBestEffort(url: string, body: unknown): Promise<void> {
  try {
    await fetch(url, {
      method: 'POST',
      headers: jsonHeaders(),
      credentials: 'same-origin',
      body: JSON.stringify(body),
    })
  } catch {
    // best-effort — the next successful sync (or a future reload) catches up.
  }
}

export async function syncHighlights(
  versionId: number,
  entries: Partial<ReaderRenderHighlight>[],
): Promise<ReaderRenderHighlight[]> {
  const body = {
    manual_version_id: versionId,
    entries: entries.map((h) => ({
      id: h.id,
      sub_para_id: h.subParaId,
      block_id: h.blockId,
      range_start_char: h.startChar,
      range_end_char: h.endChar,
      color: h.color,
      style: h.style ?? 'mark',
      note: null,
      created_at: Date.now(),
      updated_at: Date.now(),
      deleted_at: null,
    })),
  }
  try {
    const res = await fetch('/web/sync/highlights', {
      method: 'POST',
      headers: jsonHeaders(),
      credentials: 'same-origin',
      body: JSON.stringify(body),
    })
    if (!res.ok) return []
    const data = (await res.json()) as {
      merged: Array<{
        id: string
        sub_para_id: string
        block_id: string
        range_start_char: number
        range_end_char: number
        color: string
        style?: string
        deleted_at: number | null
      }>
    }
    return data.merged
      .filter((h) => h.deleted_at == null)
      .map((h) => ({
        id: h.id,
        subParaId: h.sub_para_id,
        blockId: h.block_id,
        startChar: h.range_start_char,
        endChar: h.range_end_char,
        color: h.color as ReaderRenderHighlightColor,
        style: (h.style ?? 'mark') as ReaderRenderHighlightStyle,
      }))
  } catch {
    return []
  }
}

export async function deleteHighlight(versionId: number, highlightId: string): Promise<void> {
  await postBestEffort('/web/sync/highlights', {
    manual_version_id: versionId,
    entries: [{ id: highlightId, deleted_at: Date.now(), updated_at: Date.now() }],
  })
}

export async function syncProgress(
  versionId: number,
  entries: Array<{
    sub_para_id: string
    completed: boolean
    scroll_pos: number | null
    review: boolean
  }>,
): Promise<void> {
  await postBestEffort('/web/sync/progress', {
    manual_version_id: versionId,
    entries: entries.map((e) => ({
      ...e,
      updated_at: Date.now(),
      completed_at: e.completed ? Date.now() : null,
    })),
  })
}

export async function syncReadingPosition(versionId: number, sectionId: string): Promise<void> {
  await postBestEffort('/web/sync/reading_position', {
    manual_version_id: versionId,
    section_id: sectionId,
    updated_at: Date.now(),
  })
}
